/**
 * MedicalReportsPage (Phase-2, Module 2)
 *
 * Officer / Staff / Doctor upload medical reports (PDF/image), list, search,
 * filter by type, preview/download. Mirrors the backend OfficerMedicalReport API.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Description as ReportIcon, Upload as UploadIcon, Visibility as ViewIcon, Verified as VerifyIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, FileUpload, ConfirmDialog, OfficerSelector } from '../../components/common';
import { OfficerMedicalReportApi, toAbsoluteFileUrl } from '../../services/phase2Api';
import { REPORT_TYPES, labelFor } from '../../utils/phase2Constants';
import type { OfficerMedicalReport } from '../../types/phase2';
import { useCurrentOfficer } from '../../hooks/useCurrentOfficer';
import { useNavigate, useLocation } from 'react-router-dom';

const MedicalReportsPage: React.FC = () => {
  const { officerId } = useCurrentOfficer();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<OfficerMedicalReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [delId, setDelId] = useState<number | null>(null);
  const [form, setForm] = useState({
    reportTitle: '',
    reportType: 'BLOOD_REPORT',
    hospitalName: '',
    doctorName: '',
    reportDate: new Date().toISOString().slice(0, 10),
    description: '',
    tags: '',
  });

  const load = useCallback(async () => {
    if (!officerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await OfficerMedicalReportApi.getByOfficer(officerId);
      setRows(data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load medical reports');
    } finally {
      setLoading(false);
    }
  }, [officerId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = rows.filter((r) => {
    if (typeFilter && r.reportType !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        r.reportTitle?.toLowerCase().includes(q) ||
        r.hospitalName?.toLowerCase().includes(q) ||
        r.doctorName?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleUpload = async () => {
    if (!officerId || !file || !form.reportTitle) return;
    setUploading(true);
    setError(null);
    try {
      await OfficerMedicalReportApi.upload(file, {
        officerId,
        reportTitle: form.reportTitle,
        reportType: form.reportType,
        hospitalName: form.hospitalName || undefined,
        doctorName: form.doctorName || undefined,
        reportDate: form.reportDate,
        description: form.description || undefined,
        tags: form.tags || undefined,
      });
      setOpen(false);
      setFile(null);
      setForm({ reportTitle: '', reportType: 'BLOOD_REPORT', hospitalName: '', doctorName: '', reportDate: new Date().toISOString().slice(0, 10), description: '', tags: '' });
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (delId == null) return;
    try {
      await OfficerMedicalReportApi.delete(delId);
      setDelId(null);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Delete failed');
      setDelId(null);
    }
  };

  const columns: GridColDef[] = [
    { field: 'reportTitle', headerName: 'Title', flex: 1.4, minWidth: 180 },
    {
      field: 'reportType', headerName: 'Type', width: 150,
      renderCell: (p) => <Chip size="small" label={labelFor(REPORT_TYPES, p.value)} variant="outlined" />,
    },
    { field: 'hospitalName', headerName: 'Hospital', flex: 1, minWidth: 140 },
    { field: 'doctorName', headerName: 'Doctor', width: 140 },
    { field: 'reportDate', headerName: 'Date', width: 120 },
    {
      field: 'status', headerName: 'Status', width: 130,
      renderCell: (p) => <StatusBadge status={p.value} size="small" />,
    },
    {
      field: 'document', headerName: 'Document', width: 150, sortable: false,
      renderCell: (p) => {
        const url = toAbsoluteFileUrl(p.row.fileUrl);
        if (!url) return <Chip size="small" label="No file" variant="outlined" />;
        const isImage = !!p.row.mimeType?.includes('image');
        const label = isImage ? 'View Image' : 'View PDF';
        return (
          <Tooltip title={p.row.fileName || 'Open / download document'}>
            <Button size="small" variant="outlined" startIcon={<ViewIcon />} href={url} target="_blank" rel="noopener noreferrer"
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {label}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions', headerName: 'Actions', width: 90, sortable: false,
      renderCell: (p) => (
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => setDelId(p.row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <PageScaffold
      title="Medical Reports"
      subtitle="Upload and manage your medical reports (PDF / image)"
      icon={<ReportIcon />}
      actionLabel={officerId ? 'Upload Report' : undefined}
      actionIcon={<UploadIcon />}
      onAction={officerId ? () => setOpen(true) : undefined}
      loading={loading && rows.length === 0}
      error={error}
      onDismissError={() => setError(null)}
    >
      {!officerId && (
        <OfficerSelector onSelect={(id) => navigate(`${location.pathname}?officerId=${id}`)} />
      )}
      {officerId && (
      <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search title / hospital / doctor" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {REPORT_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ height: 520, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid
          rows={filtered}
          columns={columns}
          getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          disableRowSelectionOnClick
        />
      </Box>
      </>
      )}

      <Dialog open={open} onClose={() => !uploading && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Medical Report</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Report Title" required value={form.reportTitle}
              onChange={(e) => setForm({ ...form, reportTitle: e.target.value })} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select label="Report Type" value={form.reportType}
                onChange={(e) => setForm({ ...form, reportType: e.target.value })}>
                {REPORT_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Hospital" value={form.hospitalName} fullWidth
                onChange={(e) => setForm({ ...form, hospitalName: e.target.value })} />
              <TextField label="Doctor" value={form.doctorName} fullWidth
                onChange={(e) => setForm({ ...form, doctorName: e.target.value })} />
            </Box>
            <TextField type="date" label="Report Date" InputLabelProps={{ shrink: true }} value={form.reportDate}
              onChange={(e) => setForm({ ...form, reportDate: e.target.value })} fullWidth />
            <TextField label="Description" multiline minRows={2} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />
            <FileUpload onFileSelected={setFile} uploading={uploading} label="Select report file (PDF / image)" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload}
            disabled={!officerId || !file || !form.reportTitle || uploading} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={delId != null}
        title="Delete medical report?"
        message="This will permanently remove the report and its file. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onClose={() => setDelId(null)}
      />
    </PageScaffold>
  );
};

export default MedicalReportsPage;